/**
 * Shared chrome for tool calls in the chat: one flat line per call (icon,
 * label, muted meta, chevron), expanding to a quiet detail panel.
 *
 * Behaviour follows Cline's one-line tool rows; the look (no cards, a single
 * shimmer on the active label, args/result in a hairline panel) follows the
 * Tool / Shimmer components of Vercel AI Elements (https://elements.ai-sdk.dev, Apache-2.0).
 * Nothing is vendored — this is our own Tailwind restyle in that spirit.
 */
import type { ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';

/** Markdown markers out, whitespace collapsed: labels and previews are plain text. */
export function plainText(text: string | undefined | null): string {
  if (!text) return '';
  return text
    .replace(/`{1,3}([^`]*)`{1,3}/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__)(.*?)\1/g, '$2')
    .replace(/(^|[\s(])[*_]([^*_\n]+)[*_](?=[\s).,!?:;]|$)/g, '$1$2')
    .replace(/^\s{0,3}(#{1,6}|>|[-*+]|\d+\.)\s+/gm, '')
    .replace(/[*_`]+/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Gradient-text sweep on the one label that is actively working. */
export function Shimmer({ children, active = true, className = '' }: { children: ReactNode; active?: boolean; className?: string }) {
  return <span className={`${active ? 'ai-shimmer' : ''} ${className}`}>{children}</span>;
}

export type ToolRowTone = 'default' | 'error' | 'warn';

interface ToolRowProps {
  /** A 14px lucide icon element (already sized). */
  icon: ReactNode;
  label: ReactNode;
  /** Muted secondary text after the label (a file size, a reason); shrinks first. */
  note?: string;
  /** Muted text after the label: a duration, a count, "Failed". */
  meta?: ReactNode;
  tone?: ToolRowTone;
  running?: boolean;
  open: boolean;
  onToggle?: () => void;
  /** Controls beside the row (e.g. Stop) — kept outside the toggle button. */
  actions?: ReactNode;
  /** Shown when open. */
  children?: ReactNode;
  mono?: boolean;
  title?: string;
  className?: string;
}

export function ToolRow({
  icon, label, note, meta, tone = 'default', running, open, onToggle, actions, children, mono, title, className = '',
}: ToolRowProps) {
  // Status colour lives on the icon and the meta; the label stays neutral.
  const labelColor = 'text-zinc-600';
  const expandable = !!onToggle && children !== undefined && children !== null && children !== false;
  return (
    <div className={`ai-tool-row my-0.5 ${className}`}>
      <div className="group/row flex min-w-0 items-center gap-1">
        <button
          type="button"
          onClick={expandable ? onToggle : undefined}
          aria-expanded={expandable ? open : undefined}
          title={title}
          className={`flex min-w-0 max-w-full items-center gap-2 rounded-md py-1 text-left text-meta transition-colors ${
            expandable ? 'cursor-pointer hover:text-zinc-900' : 'cursor-default'
          }`}
        >
          <span className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center [&>svg]:h-3.5 [&>svg]:w-3.5 ${running ? 'text-accent-500' : 'text-zinc-400'}`}>{icon}</span>
          <span className={`min-w-0 truncate ${mono ? 'font-mono text-xs' : ''} ${running ? '' : labelColor}`}>
            {running ? <Shimmer>{label}</Shimmer> : label}
          </span>
          {note && <span className="min-w-0 shrink-[10] truncate text-zinc-400">· {note}</span>}
          {meta && <span className="shrink-0 text-xs text-zinc-400 tabular-nums">{meta}</span>}
          {expandable && (
            <ChevronRight
              className={`h-3.5 w-3.5 shrink-0 text-zinc-300 transition-transform group-hover/row:text-zinc-500 ${open ? 'rotate-90' : ''}`}
            />
          )}
        </button>
        {actions}
      </div>
      {open && expandable && <div className="mb-2 mt-1 pl-[22px]">{children}</div>}
    </div>
  );
}

/** A small icon button for row actions (Stop, Open). */
export function RowAction({ onClick, title, children }: { onClick: () => void; title: string; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      className="flex h-6 shrink-0 items-center gap-1 rounded-md px-1.5 text-xs text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-800"
    >
      {children}
    </button>
  );
}

/** Args / result / output: light panel, hairline border, 12px mono. */
export function DetailPanel({ label, children, tone = 'default', className = '' }: {
  label?: string;
  children: ReactNode;
  tone?: 'default' | 'error';
  className?: string;
}) {
  return (
    <div className={`rounded-md border border-zinc-200 bg-zinc-50 ${className}`}>
      {label && <div className={`border-b border-zinc-200 px-2.5 py-1 text-xs ${tone === 'error' ? 'text-red-600' : 'text-zinc-500'}`}>{label}</div>}
      <div className="px-2.5 py-2">{children}</div>
    </div>
  );
}

export function DetailPre({ children, tone = 'default', maxH = 'max-h-[240px]' }: { children: ReactNode; tone?: 'default' | 'error'; maxH?: string }) {
  return (
    <pre className={`${maxH} overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-relaxed ${tone === 'error' ? 'text-red-700' : 'text-zinc-700'}`}>
      {children}
    </pre>
  );
}

/** Deliverables (images, renders, tables, HTML artifacts) keep a simple surface. */
export const DELIVERABLE_SURFACE = 'my-2 rounded-md border border-zinc-200 bg-white';
